import React, { ReactNode, useContext, useEffect, useState } from 'react';

import UbiquitiHostDeviceDetailsContext from '@totem/components/ubiquiti/hosts/details/devices/deviceDetails/ubiquitiHostDeviceDetailsContext';
import {
  IBDUniFiDevice,
  UbiquitiApiDataItem,
} from '@totem/components/ubiquiti/types';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { UBIQUITI_HOSTS_ENDPOINT } from '@totem/utilities/endpoints';

type Props = {
  hostId: string;
  siteId: string;
  deviceId: string;
  children?: ReactNode;
};

// Standalone by design (takes hostId/siteId/deviceId as props, not from route
// params) so it can be embedded anywhere a single UniFi device's full
// details/statistics are needed, not just the devices table's expanded row.
const UbiquitiHostDeviceDetailsContainer = ({
  hostId,
  siteId,
  deviceId,
  children,
}: Props) => {
  const { getApiToken } = useContext(UserProfileContext);

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const [deviceDetails, setDeviceDetails] = useState<IBDUniFiDevice | null>(
    null,
  );

  useEffect(() => {
    setLoading(true);

    setError(false);

    // IDs contain a literal colon (e.g. "D8B3...:1815601434"), which is legal
    // in a URL path segment per RFC 3986 but not guaranteed to survive every
    // intermediate proxy/gateway unencoded — encode defensively.
    fetch(
      `${UBIQUITI_HOSTS_ENDPOINT}/${encodeURIComponent(
        hostId,
      )}/sites/${encodeURIComponent(siteId)}/devices/${encodeURIComponent(
        deviceId,
      )}`,
      {
        method: 'GET',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
      },
    )
      .then((res) => {
        // A non-2xx response body is an APIErrorResponse object, not a
        // device details array — never hand that to consumers.
        if (!res.ok) {
          setError(true);
          return [];
        }
        return res.json();
      })
      .then((result: UbiquitiApiDataItem<IBDUniFiDevice>[]) => {
        setDeviceDetails(
          Array.isArray(result) && result.length > 0 ? result[0].data : null,
        );
      })
      .then(() => {
        setLoading(false);
      })
      .catch(() => {
        setError(true);

        setDeviceDetails(null);

        setLoading(false);
      });
  }, [hostId, siteId, deviceId, getApiToken]);

  return (
    <UbiquitiHostDeviceDetailsContext.Provider
      value={{ loading, error, deviceDetails }}
    >
      <div>{children}</div>
    </UbiquitiHostDeviceDetailsContext.Provider>
  );
};

export default UbiquitiHostDeviceDetailsContainer;
